import { logger } from '@/shared/libs/logger';
import {
  type AuthTokens,
  getAuthCookieDomain,
  readAuthTokens,
  writeAuthTokens,
} from './auth-cookie';

const AUTH_URL_PARAMS = ['access_token', 'refresh_token'];

export function hasAuthParamsInUrl(search: string = globalThis.location.search): boolean {
  const params = new URLSearchParams(search);
  return AUTH_URL_PARAMS.some((param) => params.has(param));
}

/** Drops credential-bearing params, leaving the rest of the URL intact. */
export function stripAuthParams(url: string | null): string | null {
  if (!url || !URL.canParse(url)) return url;

  const parsed = new URL(url);
  AUTH_URL_PARAMS.forEach((param) => parsed.searchParams.delete(param));
  return parsed.toString();
}

function readTokenPair(search: string): AuthTokens | null {
  const params = new URLSearchParams(search);
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');

  return access_token && refresh_token ? { access_token, refresh_token } : null;
}

/**
 * Removes the tokens tg-login-ui may have left in the URL and hands them back.
 * Returns null for a half-present pair, which is unusable but still stripped.
 *
 * Must run before Datadog initialises and before the router is created: both
 * snapshot the URL on the way up and never see a later replaceState, so
 * stripping during render would leave the token in RUM view URLs and in router
 * state — where `useSearchParams` writes would put it back in the address bar.
 */
export function takeUrlTokens(): AuthTokens | null {
  if (!hasAuthParamsInUrl()) return null;

  const tokens = readTokenPair(globalThis.location.search);

  const strippedUrl = stripAuthParams(globalThis.location.href);
  if (strippedUrl) globalThis.history.replaceState(globalThis.history.state, '', strippedUrl);

  return tokens;
}

/**
 * Stores URL-borne tokens in the cookie when they can still be trusted.
 *
 * OFF: tg-login-ui still redirects with tokens and expects us to store them.
 * ON: it wrote the cookie itself, so URL tokens are stale — unless no cookie
 * exists, meaning the user crossed a flag flip mid-redirect between the two
 * apps' SDKs and would otherwise be locked out.
 */
export function adoptUrlTokens(
  tokens: AuthTokens | null,
  { secureTokenRedirect }: { secureTokenRedirect: boolean },
): void {
  if (!tokens) return;
  if (secureTokenRedirect && readAuthTokens()) return;

  // A rejected cookie here leaves the app running with no session, so the next
  // request 401s straight into a logout. Record why before that happens.
  if (!writeAuthTokens(tokens)) {
    logger.error('Auth cookie not stored on login redirect', {
      cookieDomain: getAuthCookieDomain(),
    });
  }
}
